/**
 * PdfDownloadUtils Module
 *
 * Utility functions for generating PDF files from payslip tables.
 * Handles complex PDF generation with proper styling, colors, borders, and formatting
 * for both salary and bonus payslips.
 *
 * Features:
 * - A4 landscape format PDF generation
 * - Custom colors and borders from settings
 * - Desktop layout enforcement for PDF
 * - High-quality rendering with proper scaling
 * - Japanese character support
 * - Complex table border handling
 * - Row height and cell formatting
 * - Fallback error handling
 *
 * @module PdfDownloadUtils
 */

import html2pdf from 'html2pdf.js';
import { PayslipSettings } from './SettingsUtils';

/**
 * Handles PDF download for office employee payslips.
 *
 * Generates a PDF file from the payslip table with proper styling and formatting.
 * Applies custom colors, borders, and fonts from settings.
 * Ensures desktop layout for consistent PDF rendering.
 *
 * Process:
 * 1. Validates input (table ref, year, month/period, employee code)
 * 2. Applies custom colors to original element temporarily
 * 3. Creates cloned element with PDF-specific styling
 * 4. Applies desktop layout enforcement
 * 5. Configures html2pdf options for high-quality rendering
 * 6. Generates and downloads PDF
 * 7. Cleans up temporary styles and containers
 *
 * @param tableRef - React ref to the payslip table container element
 * @param selectedYear - Selected year for payslip
 * @param selectedMonth - Selected month for salary payslip (empty for bonus)
 * @param selectedPeriod - Selected period for bonus payslip (empty for salary)
 * @param empCode - Employee code
 * @param dataType - Type of payslip ('salary' or 'bonus')
 * @param payslipSettings - Payslip appearance settings (colors, fonts, etc.)
 *
 * @example
 * ```typescript
 * handlePdfDownload(
 *   tableRef,
 *   '2024',
 *   '1',
 *   '',
 *   'EMP001',
 *   'salary',
 *   payslipSettings
 * );
 * ```
 */
export const handlePdfDownload = (
  tableRef: React.RefObject<HTMLDivElement | null>,
  selectedYear: string,
  selectedMonth: string,
  selectedPeriod: string,
  empCode: string,
  dataType: 'salary' | 'bonus',
  payslipSettings: PayslipSettings
): void => {
  if (!tableRef.current) {
    alert('テーブルが表示されていません。まず明細書を生成してください。');
    return;
  }

  if (!selectedYear || !empCode) {
    alert('年、従業員コードを入力してください。');
    return;
  }

  if (dataType === 'salary' && !selectedMonth) {
    alert('月を入力してください。');
    return;
  }

  if (dataType === 'bonus' && !selectedPeriod) {
    alert('期を入力してください。');
    return;
  }

  const element = tableRef.current;
  
  /**
   * Store original CSS custom properties for restoration after PDF generation.
   * Prevents permanent style changes to the displayed table.
   */
  const originalBorderColor = element.style.getPropertyValue('--payslip-border-color');
  const originalBackgroundColor = element.style.getPropertyValue('--payslip-background-color');
  
  /**
   * Applies custom colors to the payslip table elements.
   *
   * Sets CSS custom properties and inline styles on all table cells to ensure
   * borders and backgrounds are visible in the generated PDF.
   * Handles spanned cells (rowspan/colspan) differently to avoid internal borders.
   *
   * @param el - HTML element to apply colors to
   *
   * @private
   */
  const applyCustomColors = (el: HTMLElement) => {
    const borderColor = payslipSettings.borderColor;
    const backgroundColor = payslipSettings.backgroundColor;
    
    // Set CSS custom properties on the element for CSS variable support
    el.style.setProperty('--payslip-border-color', borderColor);
    el.style.setProperty('--payslip-background-color', backgroundColor);
    
    /**
     * Apply direct inline styles to all table cells to ensure borders are visible in PDF.
     * html2pdf.js sometimes doesn't render CSS variables correctly, so inline styles are needed.
     */
    const allTableCells = el.querySelectorAll('td, th');
    allTableCells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;
      
      // Apply border color to all cells
      htmlCell.style.setProperty('border-color', borderColor);
      htmlCell.style.setProperty('background-color', backgroundColor);
      htmlCell.style.setProperty('border-style', 'solid');
      
      /**
       * Handle spanned cells differently to avoid internal borders.
       * Spanned cells (rowspan/colspan) require special border handling to prevent
       * double borders at merge points.
       */
      if (htmlCell.hasAttribute('rowspan') || htmlCell.hasAttribute('colspan')) {
        // For spanned cells, only apply outer borders
        htmlCell.style.setProperty('border-width', '1px');
        htmlCell.style.setProperty('border-collapse', 'separate');
        htmlCell.style.setProperty('border-spacing', '0');
        
        /**
         * Special handling for vertical text cells with rowspan.
         * Vertical text cells need additional styling to ensure proper rendering in PDF.
         */
        if (htmlCell.classList.contains('vertical-text') && htmlCell.hasAttribute('rowspan')) {
          htmlCell.style.setProperty('border-top', `1px solid ${borderColor}`);
          htmlCell.style.setProperty('border-left', `1px solid ${borderColor}`);
          htmlCell.style.setProperty('border-right', `1px solid ${borderColor}`);
          htmlCell.style.setProperty('border-bottom', `1px solid ${borderColor}`);
          htmlCell.style.setProperty('border-inline', 'none');
          htmlCell.style.setProperty('border-block', 'none');
          htmlCell.style.setProperty('border-image', 'none');
          htmlCell.style.setProperty('position', 'relative');
          htmlCell.style.setProperty('z-index', '10');
          htmlCell.style.setProperty('background-clip', 'padding-box');
        }
      } else {
        // For regular cells, apply normal borders
        htmlCell.style.setProperty('border-width', '1px');
      }
      
      htmlCell.style.setProperty('vertical-align', 'middle');
      htmlCell.style.setProperty('text-align', 'center');
      
    });
    
    // Also apply CSS custom properties to all elements
    const allElements = el.querySelectorAll('*');
    allElements.forEach((elem: Element) => {
      const htmlElem = elem as HTMLElement;
      htmlElem.style.setProperty('--payslip-border-color', borderColor);
      htmlElem.style.setProperty('--payslip-background-color', backgroundColor);
    });
    
  };
  
  // Apply colors to the original element
  applyCustomColors(element);
  
  /**
   * Create a temporary container and clone for PDF generation.
   *
   * The cloned element is positioned off-screen to avoid affecting the displayed table.
   * Uses high-quality dimensions optimized for A4 landscape format.
   */
  const tempContainer = document.createElement('div');
  tempContainer.style.cssText = `
    position: fixed;
    top: -9999px;
    left: -9999px;
    width: 1123px;
    height: 794px;
    overflow: hidden;
    background: white;
    z-index: -1;
  `;

  /**
   * Clone the element for PDF generation with high-quality dimensions.
   * The clone allows us to apply PDF-specific styling without affecting the original table.
   */
  const clonedElement = element.cloneNode(true) as HTMLElement;
  clonedElement.style.cssText = `
    width: 1080px;
    max-width: 1080px;
    height: auto;
    max-height: 750px;
    overflow: hidden;
    transform: scale(0.9);
    transform-origin: top left;
    box-sizing: border-box;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    margin: 0 !important;
    padding: 0 !important;
  `;
  
  // Add PDF-specific class to cloned element only (for CSS targeting)
  clonedElement.classList.add('pdf-generation');
  
  /**
   * Apply desktop layout to cloned element's containers - FORCE DESKTOP LAYOUT.
   *
   * Ensures the PDF always uses desktop layout even if the browser is in mobile view.
   * This is critical for consistent PDF rendering across different screen sizes.
   */
  const clonedContainer = clonedElement.closest('.office-emp-preview') as HTMLElement;
  const clonedTableContainer = clonedElement.closest('.payslip-table-container') as HTMLElement;
  const clonedTableArea = clonedElement.closest('.table-area') as HTMLElement;
  const clonedPayslipWrapper = clonedElement.closest('.payslip-scale-wrapper') as HTMLElement;
  const clonedPayslipInner = clonedElement.closest('.payslip-scale-inner') as HTMLElement;
  
  if (clonedContainer) {
    clonedContainer.style.padding = '0px !important';
    clonedContainer.style.margin = '0px !important';
    clonedContainer.style.overflowX = 'visible';
    clonedContainer.style.overflowY = 'visible';
    clonedContainer.style.minWidth = 'auto';
    clonedContainer.style.maxWidth = 'none';
  }
  
  if (clonedTableContainer) {
    clonedTableContainer.style.padding = '0px !important';
    clonedTableContainer.style.margin = '0px !important';
    clonedTableContainer.style.overflowX = 'visible';
    clonedTableContainer.style.overflowY = 'visible';
    clonedTableContainer.style.minWidth = 'auto';
    clonedTableContainer.style.maxWidth = 'none';
  }

  if (clonedTableArea) {
    clonedTableArea.style.padding = '0px !important';
    clonedTableArea.style.margin = '0px !important';
    clonedTableArea.style.marginTop = '0px !important';
    clonedTableArea.style.overflowX = 'visible';
    clonedTableArea.style.overflowY = 'visible';
  }

  if (clonedPayslipWrapper) {
    clonedPayslipWrapper.style.setProperty('--scale', '1', 'important');
    clonedPayslipWrapper.style.overflowX = 'visible';
    clonedPayslipWrapper.style.overflowY = 'visible';
  }

  if (clonedPayslipInner) {
    clonedPayslipInner.style.transform = 'scale(1) !important';
    clonedPayslipInner.style.transformOrigin = 'top left';
  }

  tempContainer.appendChild(clonedElement);
  document.body.appendChild(tempContainer);
  
  /**
   * Store references for cleanup after PDF generation.
   * These references allow us to remove the temporary container even if the PDF generation fails.
   */
  (element as any).tempContainer = tempContainer;
  (element as any).clonedElement = clonedElement;
  
  /**
   * Applies PDF-specific styling to ensure proper A4 landscape fitting.
   *
   * Sets dimensions, scaling, and styling optimized for PDF generation.
   * Ensures all cells are visible, properly sized, and formatted for PDF rendering.
   *
   * @param el - HTML element to apply PDF styling to
   *
   * @private
   */
  const applyPdfStyling = (el: HTMLElement) => {
    // Add PDF-specific class for styling
    el.classList.add('pdf-generation');
    
    // High-quality dimensions for crisp rendering
    el.style.width = '1080px';
    el.style.maxWidth = '1080px';
    el.style.maxHeight = '750px'; // Slightly increased height for visibility
    el.style.overflow = 'hidden'; // Prevent content overflow
    el.style.margin = '0 !important';
    el.style.padding = '0 !important';
    el.style.display = 'block';
    el.style.transformOrigin = 'top left';
    el.style.transform = 'scale(0.9)'; // Slightly increased scale for better quality
    el.style.boxSizing = 'border-box';
    el.style.setProperty('-webkit-font-smoothing', 'antialiased');
    el.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
    
    // Force desktop layout - override any mobile CSS
    // This ensures consistent PDF rendering regardless of browser viewport size
    el.style.setProperty('--scale', '1', 'important');
    
    /**
     * Find and override mobile-specific containers.
     * Ensures all parent containers use desktop layout for PDF generation.
     */
    const allContainers = el.querySelectorAll('.office-emp-preview, .table-area, .payslip-scale-wrapper, .payslip-scale-inner, .payslip-container');
    allContainers.forEach((container: Element) => {
      const htmlContainer = container as HTMLElement;
      htmlContainer.style.padding = '0 !important';
      htmlContainer.style.margin = '0 !important';
      htmlContainer.style.marginTop = '0 !important';
      htmlContainer.style.setProperty('--scale', '1', 'important');
      htmlContainer.style.transform = 'scale(1) !important';
      htmlContainer.style.overflowX = 'visible';
      htmlContainer.style.overflowY = 'visible';
    });
    
    /**
     * Ensure empty cells are visible in PDF - but be gentle with the structure.
     * Adds non-breaking spaces to empty cells to maintain row height in PDF,
     * but only if the cell is truly empty and not a no-border cell.
     */
    const allCells = el.querySelectorAll('td, th');
    allCells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;
      
      // Only add non-breaking space if cell is truly empty (to maintain row height in PDF)
      if (htmlCell.textContent?.trim() === '' && !htmlCell.classList.contains('no-border')) {
        htmlCell.innerHTML = '&nbsp;';
      }
      
      /**
       * Ensure consistent row height for all cells.
       * This prevents rows from collapsing in the PDF output.
       */
      htmlCell.style.minHeight = '26px';
      htmlCell.style.maxHeight = '28px';
      
      // High-quality styling for crisp text rendering
      htmlCell.style.padding = '4px 5px';
      htmlCell.style.fontSize = '10px';
      htmlCell.style.lineHeight = '1.4';
      htmlCell.style.fontWeight = '600';
      htmlCell.style.color = '#000000';
      htmlCell.style.textRendering = 'optimizeLegibility';
      htmlCell.style.fontFamily = 'Arial, sans-serif';
      htmlCell.style.minHeight = '20px';
      htmlCell.style.maxHeight = '28px';
      htmlCell.style.overflow = 'hidden';
      htmlCell.style.textOverflow = 'ellipsis';
      htmlCell.style.setProperty('-webkit-font-smoothing', 'antialiased');
      htmlCell.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
      htmlCell.style.setProperty('text-shadow', 'none');
    });
    
    // Preserve vertical text cells with enhanced styling
    const verticalTextCells = el.querySelectorAll('.vertical-text');
    verticalTextCells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;
      htmlCell.style.writingMode = 'vertical-rl';
      htmlCell.style.textOrientation = 'mixed';
      htmlCell.style.height = 'auto';
      htmlCell.style.minHeight = '60px';
      htmlCell.style.fontWeight = '600';
      htmlCell.style.fontSize = '11px';
    });
    
    // Enhance sub-title cells
    const subTitleCells = el.querySelectorAll('.sub-title-cell');
    subTitleCells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;
      htmlCell.style.fontSize = '12px';
      htmlCell.style.fontWeight = '700';
      htmlCell.style.color = '#0d4d0d';
    });
  };
  
  // Use the cloned element for PDF generation (not the original)
  const pdfElement = clonedElement;
  
  /**
   * Applies office-specific PDF styling.
   *
   * Handles complex styling requirements for office employee payslips:
   * - Table border styling
   * - Section-specific borders (支給, 控除, 勤怠, その他)
   * - Row background colors (green rows, white rows)
   * - Header row styling
   * - Memo section styling
   * - Text rendering quality
   *
   * @param el - HTML element to apply office-specific PDF styling to
   *
   * @private
   */
  const applyOfficePdfStyling = (el: HTMLElement) => {
    // High-quality table styling for crisp rendering
    const tables = el.querySelectorAll('table');
    tables.forEach((table: Element) => {
      const htmlTable = table as HTMLElement;
      htmlTable.style.width = '100%';
      htmlTable.style.maxWidth = '1100px';
      htmlTable.style.borderCollapse = 'collapse';
      htmlTable.style.tableLayout = 'auto'; // Use auto for better content display
      htmlTable.style.fontSize = '10px';
      htmlTable.style.fontFamily = 'Arial, sans-serif';
      htmlTable.style.minHeight = '200px'; // Ensure minimum visibility
      htmlTable.style.setProperty('-webkit-font-smoothing', 'antialiased');
      htmlTable.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
    });
    
    // Apply proper border styling based on original CSS classes
    const allCells = el.querySelectorAll('td, th');
    allCells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;
      
      // Apply crisp border for all cells with better rendering
      htmlCell.style.border = '1px solid var(--payslip-border-color, #4caf50)';
      htmlCell.style.borderWidth = '1px';
      htmlCell.style.borderStyle = 'solid';
      htmlCell.style.setProperty('border-image', 'none');
      
      // Enhanced rendering for memo section cells
      if (htmlCell.classList.contains('memo') || 
          htmlCell.classList.contains('memo-cell') ||
          htmlCell.classList.contains('memo-top-border') ||
          htmlCell.classList.contains('memo-bottom-border')) {
        htmlCell.style.setProperty('-webkit-print-color-adjust', 'exact');
        htmlCell.style.setProperty('print-color-adjust', 'exact');
        htmlCell.style.setProperty('-webkit-font-smoothing', 'antialiased');
        htmlCell.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
        htmlCell.style.setProperty('text-rendering', 'optimizeLegibility');
        htmlCell.style.setProperty('image-rendering', '-webkit-optimize-contrast');
        htmlCell.style.setProperty('image-rendering', 'crisp-edges');
      }
      
      // Ensure white-row cells maintain white background
      const cellText = htmlCell.textContent?.trim() || '';
      const isWhiteRow = htmlCell.closest('.white-row') || htmlCell.closest('.all-white-row');
      
      /**
       * Check if this row contains header labels (組織コード, 社員コード, 氏名).
       * Data rows (rows after header rows) should have white background in PDF.
       */
      const currentRow = htmlCell.closest('tr');
      let isDataRow = false;
      if (currentRow) {
        const rowCells = currentRow.querySelectorAll('td, th');
        const hasHeaderLabels = Array.from(rowCells).some(cell => {
          const text = cell.textContent?.trim() || '';
          return text.includes('組織コード') || 
                 text.includes('社員コード') || 
                 text.includes('Employee Code') || 
                 text.includes('従業員コード') ||
                 text.includes('氏名') ||
                 text.includes('Name');
        });
        
        // Check if the previous row has header labels
        const prevRow = currentRow.previousElementSibling;
        let prevRowHasHeaders = false;
        if (prevRow) {
          const prevRowCells = prevRow.querySelectorAll('td, th');
          prevRowHasHeaders = Array.from(prevRowCells).some(cell => {
            const text = cell.textContent?.trim() || '';
            return text.includes('組織コード') || 
                   text.includes('社員コード') || 
                   text.includes('Employee Code') || 
                   text.includes('従業員コード') ||
                   text.includes('氏名') ||
                   text.includes('Name');
          });
        }
        
        // If this row doesn't have header labels but the previous row does, it's a data row (white)
        isDataRow = !hasHeaderLabels && prevRowHasHeaders;
      }
      
      /**
       * Apply white background to:
       * 1. All cells in white rows
       * 2. Company name and employee name cells
       * 3. Data rows (rows that contain actual values, not headers)
       *
       * This ensures proper background colors in the generated PDF.
       */
      if (isWhiteRow ||
          htmlCell.classList.contains('company-name') ||
          htmlCell.classList.contains('employee-name') ||
          isDataRow) {
        htmlCell.style.backgroundColor = '#ffffff';
        htmlCell.style.setProperty('background-color', '#ffffff', 'important');
      }
      
      // Apply specific border styling based on classes
      if (htmlCell.classList.contains('border-rect')) {
        // border-rect cells get special treatment in original CSS
        // Don't override here, let the CSS classes handle it
      }
      
      if (htmlCell.classList.contains('no-border')) {
        htmlCell.style.border = 'none';
      }
      
      /**
       * Apply section-specific border styling.
       * Different sections (支給, 控除, 勤怠, その他) use different border styles
       * to visually separate sections in the payslip.
       */
      if (htmlCell.classList.contains('shikyuu-border-top')) {
        htmlCell.style.borderTop = '2px solid var(--payslip-border-color, #4caf50)';
      }
      if (htmlCell.classList.contains('shikyuu-border-bottom')) {
        htmlCell.style.borderBottom = '2px solid var(--payslip-border-color, #4caf50)';
      }
      if (htmlCell.classList.contains('shikyuu-border-left')) {
        htmlCell.style.borderLeft = '2px solid var(--payslip-border-color, #4caf50)';
      }
      if (htmlCell.classList.contains('shikyuu-border-right')) {
        htmlCell.style.borderRight = '2px solid var(--payslip-border-color, #4caf50)';
      }
      
      // Enhanced memo section border styling for PDF
      if (htmlCell.classList.contains('memo-cell')) {
        htmlCell.style.borderLeft = '2px solid var(--payslip-border-color, #4caf50)';
        htmlCell.style.borderRight = '2px solid var(--payslip-border-color, #4caf50)';
      }
      if (htmlCell.classList.contains('memo-top-border')) {
        htmlCell.style.borderTop = '2px solid var(--payslip-border-color, #4caf50)';
        htmlCell.style.borderBottom = 'none';
      }
      if (htmlCell.classList.contains('memo-bottom-border')) {
        htmlCell.style.borderBottom = '2px solid var(--payslip-border-color, #4caf50)';
        htmlCell.style.borderTop = 'none';
      }
      
      // Apply other section borders
      if (htmlCell.classList.contains('koujo-border-top')) {
        htmlCell.style.borderTop = '2px solid var(--payslip-border-color, #4caf50)';
      }
      if (htmlCell.classList.contains('koujo-border-bottom')) {
        htmlCell.style.borderBottom = '2px solid var(--payslip-border-color, #4caf50)';
      }
      if (htmlCell.classList.contains('kintai-border-top')) {
        htmlCell.style.borderTop = '2px solid var(--payslip-border-color, #4caf50)';
      }
      if (htmlCell.classList.contains('kintai-border-bottom')) {
        htmlCell.style.borderBottom = '2px solid var(--payslip-border-color, #4caf50)';
      }
      if (htmlCell.classList.contains('kiji-border-top')) {
        htmlCell.style.borderTop = '2px solid var(--payslip-border-color, #4caf50)';
      }
      if (htmlCell.classList.contains('kiji-border-bottom')) {
        htmlCell.style.borderBottom = '2px solid var(--payslip-border-color, #4caf50)';
      }
      
      // Enhance text quality
      htmlCell.style.color = '#1a1a1a';
      htmlCell.style.fontWeight = '500';
      htmlCell.style.textRendering = 'optimizeLegibility';
      htmlCell.style.setProperty('-webkit-font-smoothing', 'antialiased');
      htmlCell.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
    });
    
    /**
     * Apply proper row styling based on original CSS.
     * Green rows (支給 section) use custom background color.
     * White rows use white background for data rows.
     */
    const greenRows = el.querySelectorAll('.green-row');
    greenRows.forEach((row: Element) => {
      const htmlRow = row as HTMLElement;
      // Apply background color to all cells except first (which contains label)
      const cells = htmlRow.querySelectorAll('td:not(:first-child)');
      cells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        htmlCell.style.backgroundColor = 'var(--payslip-background-color, #eaffea)';
      });
    });
    
    // Apply white background to white rows (data rows)
    const whiteRows = el.querySelectorAll('.white-row, .all-white-row');
    whiteRows.forEach((row: Element) => {
      const htmlRow = row as HTMLElement;
      const cells = htmlRow.querySelectorAll('td');
      cells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        htmlCell.style.backgroundColor = '#ffffff';
        htmlCell.style.setProperty('background-color', '#ffffff', 'important');
      });
    });
    
    
    /**
     * Apply special styling for first and second rows (header rows).
     * Header rows contain 組織コード, 社員コード, 氏名 and title information.
     * These rows use border-rect cells with special border styling.
     */
    const firstRow = el.querySelector('tr:nth-child(1)');
    const secondRow = el.querySelector('tr:nth-child(2)');
    
    if (firstRow) {
      const borderRectCells = firstRow.querySelectorAll('.border-rect');
      borderRectCells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        htmlCell.style.borderTop = '2px solid var(--payslip-border-color, #4caf50)';
        htmlCell.style.borderBottom = 'none';
      });
      
      // First and last border-rect cells get side borders
      const firstBorderRect = firstRow.querySelector('.border-rect:first-child');
      const lastBorderRect = firstRow.querySelector('.border-rect:last-child');
      const thirdBorderRect = firstRow.querySelector('.border-rect:nth-of-type(3)');
      
      if (firstBorderRect) {
        (firstBorderRect as HTMLElement).style.borderLeft = '2px solid var(--payslip-border-color, #4caf50)';
      }
      if (lastBorderRect) {
        (lastBorderRect as HTMLElement).style.borderRight = '2px solid var(--payslip-border-color, #4caf50)';
      }
      if (thirdBorderRect) {
        (thirdBorderRect as HTMLElement).style.borderRight = '2px solid var(--payslip-border-color, #4caf50)';
      }
    }
    
    if (secondRow) {
      const borderRectCells = secondRow.querySelectorAll('.border-rect');
      borderRectCells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        htmlCell.style.borderBottom = '2px solid var(--payslip-border-color, #4caf50)';
        htmlCell.style.borderTop = 'none';
      });
      
      // First and last border-rect cells get side borders
      const firstBorderRect = secondRow.querySelector('.border-rect:first-child');
      const lastBorderRect = secondRow.querySelector('.border-rect:last-child');
      const thirdBorderRect = secondRow.querySelector('.border-rect:nth-of-type(3)');
      
      if (firstBorderRect) {
        (firstBorderRect as HTMLElement).style.borderLeft = '2px solid var(--payslip-border-color, #4caf50)';
      }
      if (lastBorderRect) {
        (lastBorderRect as HTMLElement).style.borderRight = '2px solid var(--payslip-border-color, #4caf50)';
      }
      if (thirdBorderRect) {
        (thirdBorderRect as HTMLElement).style.borderRight = '2px solid var(--payslip-border-color, #4caf50)';
      }
    }
  };
  
  applyOfficePdfStyling(pdfElement);
  
  /**
   * WYSIWYG capture with slight compaction for PDF sizing.
   * Adds CSS classes for PDF-specific styling that may be defined in stylesheets.
   */
  pdfElement.classList.add('pdf-compact');
  pdfElement.classList.add('pdf-compact-scale');
  
  /**
   * Balanced content height control.
   * Calculates content height with a cap to ensure PDF fits on A4 landscape page.
   */
  const contentHeight = Math.min(pdfElement.scrollHeight, 750); // Balanced height cap
  
  // Additional check to ensure content fits while maintaining visibility
  if (contentHeight > 750) {
    pdfElement.style.transform = 'scale(0.8)';
    pdfElement.style.maxHeight = '750px';
  }
  
  /**
   * html2pdf.js configuration options for high-quality PDF generation.
   *
   * Uses high scale (3.0) and DPI (400) for crisp borders and text.
   * JPEG format for smaller PDF size (balanced quality).
   * A4 landscape orientation for payslip format.
   */
  const opt = {
    margin: [8, 5, 2, 5] as [number, number, number, number], // Increased top margin for better positioning
    filename: `${dataType === 'salary' ? '給与' : '賞与'}明細書_${selectedYear}年${dataType === 'salary' ? selectedMonth + '月' : selectedPeriod}_${empCode}.pdf`,
    image: { type: 'jpeg', quality: 0.92 }, // JPEG for smaller file size (balanced quality)
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }, // Better page break handling (avoid splitting rows)
    html2canvas: { 
      scale: 3.0, // Increased scale for maximum quality and crisp borders
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 1080, // Restored width for better balance
      height: contentHeight, // Use calculated height
      scrollX: 0,
      scrollY: 0,
      letterRendering: true,
      logging: false,
      removeContainer: false,
      foreignObjectRendering: false,
      windowWidth: 1600, // Increased window width for higher resolution
      windowHeight: Math.min(contentHeight * 2, 1200), // Increased window height
      x: 0,
      y: 0,
      dpi: 400, // Increased DPI for sharper borders and text
      scaleX: 1,
      scaleY: 1,
      pixelRatio: 2, // Higher pixel ratio for crisp rendering
      ignoreElements: (element: Element) => {
        // Ignore elements that might cause rendering issues
        return element.classList.contains('pdf-generation') && (element as HTMLElement).style.display === 'none';
      }
    },
    jsPDF: { 
      unit: 'mm', 
      format: 'a4', 
      orientation: 'landscape' as 'landscape',
      compress: false, // Disable compression for maximum quality
      putOnlyUsedFonts: true,
      floatPrecision: 4 // Highest precision for best quality
    }
  };

  /**
   * Generate and download PDF.
   * After PDF generation, cleanup temporary styles and containers.
   */
  html2pdf().set(opt).from(pdfElement).save().then(() => {
    
    /**
     * No need to restore original mobile styles since we never modified the original element.
     * Only the cloned element was styled for PDF generation.
     */
    
    /**
     * Restore original colors after PDF generation.
     * Removes temporary color styles applied to the original element.
     */
    if (originalBorderColor) {
      element.style.setProperty('--payslip-border-color', originalBorderColor);
    } else {
      element.style.removeProperty('--payslip-border-color');
    }
    if (originalBackgroundColor) {
      element.style.setProperty('--payslip-background-color', originalBackgroundColor);
    } else {
      element.style.removeProperty('--payslip-background-color');
    }
    
    /**
     * Clean up inline styles from table cells.
     * Removes temporary styles applied during PDF generation process.
     */
    const allTableCells = element.querySelectorAll('td, th');
    allTableCells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;
      htmlCell.style.removeProperty('border-color');
      htmlCell.style.removeProperty('background-color');
      htmlCell.style.removeProperty('border-style');
      htmlCell.style.removeProperty('border-width');
      htmlCell.style.removeProperty('vertical-align');
      htmlCell.style.removeProperty('text-align');
      
      // Restore empty cells to original state (remove non-breaking spaces added for PDF)
      if (htmlCell.innerHTML === '&nbsp;' || htmlCell.innerHTML === '\u00A0') {
        htmlCell.innerHTML = '';
      }
    });
    
    /**
     * Clean up temporary container.
     * Removes the off-screen container used for PDF generation.
     */
    const tempContainer = (element as any).tempContainer;
    if (tempContainer && tempContainer.parentNode) {
      tempContainer.parentNode.removeChild(tempContainer);
    }
    
  }).catch((error) => {
    console.error('PDF generation error:', error);
    
    /**
     * Fallback PDF generation with more conservative settings.
     * Used when primary PDF generation fails (e.g., content too large, memory issues).
     * Uses lower scale and more aggressive compression to ensure PDF is generated.
     */
    const fallbackOpt = {
      margin: [8, 5, 2, 5] as [number, number, number, number], // Increased top margin even in fallback
      filename: `${dataType === 'salary' ? '給与' : '賞与'}明細書_${selectedYear}年${dataType === 'salary' ? selectedMonth + '月' : selectedPeriod}_${empCode}.pdf`,
      image: { type: 'jpeg', quality: 0.9 },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      html2canvas: { 
        scale: 2.5, // Increased scale for better quality even in fallback
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 1000, // Restored width for better balance
        height: 700,
        scrollX: 0,
        scrollY: 0,
        letterRendering: true,
        logging: false,
        removeContainer: false,
        foreignObjectRendering: false,
        windowWidth: 1200, // Increased window width
        windowHeight: 800, // Increased window height
        x: 0,
        y: 0,
        dpi: 350, // Increased DPI for better quality
        scaleX: 1,
        scaleY: 1,
        pixelRatio: 2 // Higher pixel ratio for crisp rendering
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'landscape' as 'landscape',
        compress: true,
        putOnlyUsedFonts: true,
        floatPrecision: 2
      }
    };
    
    // Apply more conservative styling for fallback
    pdfElement.style.transform = 'scale(0.7)';
    pdfElement.style.maxHeight = '700px';
    pdfElement.style.overflow = 'hidden';
    
    html2pdf().set(fallbackOpt).from(pdfElement).save().then(() => {
      // Clean up temporary container
      const tempContainer = (element as any).tempContainer;
      if (tempContainer && tempContainer.parentNode) {
        tempContainer.parentNode.removeChild(tempContainer);
      }
      
      // Restore original colors
      if (originalBorderColor) {
        element.style.setProperty('--payslip-border-color', originalBorderColor);
      } else {
        element.style.removeProperty('--payslip-border-color');
      }
      if (originalBackgroundColor) {
        element.style.setProperty('--payslip-background-color', originalBackgroundColor);
      } else {
        element.style.removeProperty('--payslip-background-color');
      }
      
      // Clean up inline styles from table cells
      const allTableCells = element.querySelectorAll('td, th');
      allTableCells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        htmlCell.style.removeProperty('border-color');
        htmlCell.style.removeProperty('background-color');
        htmlCell.style.removeProperty('border-style');
        htmlCell.style.removeProperty('border-width');
        htmlCell.style.removeProperty('vertical-align');
        htmlCell.style.removeProperty('text-align');
        
        // Restore empty cells to original state
        if (htmlCell.innerHTML === '&nbsp;' || htmlCell.innerHTML === '\u00A0') {
          htmlCell.innerHTML = '';
        }
      });
    }).catch((fallbackError) => {
      console.error('Fallback PDF generation also failed:', fallbackError);
      alert('PDFの生成に失敗しました。エラー: ' + error.message);
      
      // Clean up temporary container even on fallback error
      const tempContainer = (element as any).tempContainer;
      if (tempContainer && tempContainer.parentNode) {
        tempContainer.parentNode.removeChild(tempContainer);
      }
      
      // Restore original colors even on fallback error
      if (originalBorderColor) {
        element.style.setProperty('--payslip-border-color', originalBorderColor);
      } else {
        element.style.removeProperty('--payslip-border-color');
      }
      if (originalBackgroundColor) {
        element.style.setProperty('--payslip-background-color', originalBackgroundColor);
      } else {
        element.style.removeProperty('--payslip-background-color');
      }
      
      // Clean up inline styles from table cells even on fallback error
      const allTableCells = element.querySelectorAll('td, th');
      allTableCells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        htmlCell.style.removeProperty('border-color');
        htmlCell.style.removeProperty('background-color');
        htmlCell.style.removeProperty('border-style');
        htmlCell.style.removeProperty('border-width');
        htmlCell.style.removeProperty('vertical-align');
        htmlCell.style.removeProperty('text-align');
        
        // Restore empty cells to original state
        if (htmlCell.innerHTML === '&nbsp;' || htmlCell.innerHTML === '\u00A0') {
          htmlCell.innerHTML = '';
        }
      });
    });
  });
};
